import {
  BaseControlItem,
  CanopyProvider,
  ControlPanel,
  ControlSection,
  FilterIcon,
  Select,
} from '@bloomfieldrobotics/canopy';
import { Box, IconButton, Stack } from '@mui/material';
import { DatasetElementType } from '@mui/x-charts/internals';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppSelector } from '../../../app/hooks';
import { useStages } from '../../../services/entities/hooks/useStages';
import { useVariety } from '../../../services/entities/hooks/useVariety';
import { useTranslation } from '../../../services/translation/hooks/useTranslation';
import { BetaWrapper } from '../../commonUI/BetaWrapper/BetaWrapper';
import { CustomerControlSection } from '../../commonUI/ControlSections/CustomerControlSection';
import { RanchToggleControlSection } from '../../commonUI/ControlSections/RanchToggleControlSection';
import { StrictDateRange } from '../../commonUI/DateRangeFilterSelect/types';
import { generateDateRangeFilter } from '../../commonUI/DateRangeFilterSelect/utils/generateDateRangeFilter';
import { useDebouncedLoading } from '../../commonUI/hooks/useDebouncedLoading';
import { MenuPaper } from '../../commonUI/Menu/MenuPaper';
import { DiagnosticFeature } from '../../commonUI/viewMode/DiagnosticFeature';
import { DebugIcon } from '../../navbar/components/icons/DebugIcon';
import { useLocationState } from '../../router/hooks/useLocationState';
import { selectCurrCustomerID } from '../../select/selectSlice';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { ChartsLoadingOverlay } from '../components/ChartsLoadingOverlay';
import { NoDataOverlay } from '../components/NoDataOverlay';
import { ReportChartContainer } from '../components/ReportBarChart/components/ReportChartContainer';
import { CountMode } from '../components/ReportBarChart/types';
import { useNavigateWithReportDateRange } from '../hooks/useNavigateWithReportDateRange';
import { SizeBucket, sizeBucketLabelMap } from './components/Charts/SizeBucket';
import { SizeReportBarChart } from './components/Charts/SizeReportBarChart';
import { StageReportBarChart } from './components/Charts/StageReportBarChart';
import { CountModeToggleButtonGroup } from './components/CountModeToggleButtonGroup';
import { DownloadSizeReportButton } from './components/DownloadSizeReportButton';
import { DownloadStageReportButton } from './components/DownloadStageReportButton';
import { RawCountsDialog } from './components/RawCounts/RawCountsDialog';
import { ReportsDateRangeSelect } from './components/ReportsDateRangeSelect';
import { SizeSelect } from './components/SizeSelect';
import { StageSelect } from './components/StageSelect';
import { useBlueberryReportDateRangeKeys } from './hooks/useBlueberryReportDateRangeKeys';
import { useChartDates } from './hooks/useChartDates';
import { useLoadVarietySizeReport } from './hooks/useLoadVarietySizeReport';
import { useLoadVarietyStageReport } from './hooks/useLoadVarietyStageReport';

type ViewBy = 'size' | 'stage';

export const VarietyReportPage = () => {
  const customerId = useAppSelector(selectCurrCustomerID);

  const dateRangeOptions = useBlueberryReportDateRangeKeys();
  const { dateRangeKey } = useLocationState();
  const navigate = useNavigateWithReportDateRange(
    dateRangeOptions,
    dateRangeKey
  );
  const [dateRange, setDateRange] = useState<StrictDateRange | null>(
    generateDateRangeFilter(dateRangeKey ?? 'week')
  );

  const [ranchesSelected, setRanchesSelected] = useState<string[]>([]);
  const [stagesSelected, setStagesSelected] = useState<string[]>([]);
  const [sizesSelected, setSizesSelected] = useState<SizeBucket[]>(
    Object.keys(sizeBucketLabelMap)
  );

  const [rawCountsOpen, setRawCountsOpen] = useState(false);

  const { t } = useTranslation();

  const [viewBy, setViewBy] = useState<ViewBy>('stage'); // This is a temporary solution while size and stages must be shown seperately
  const [countMode, setCountMode] = useState<CountMode>('total');

  // Auto-switch to 'total' mode when viewing by size since per plant data is not available for size reports
  useEffect(() => {
    if (viewBy === 'size' && countMode === 'per_plant') {
      setCountMode('total');
    }
  }, [viewBy, countMode]);

  const { varietyId } = useParams<{ varietyId: string }>();
  const { variety } = useVariety(varietyId);

  const { stages: stageDefinitions } = useStages(varietyId);
  // Show all stages by default
  useEffect(() => {
    setStagesSelected(stageDefinitions.map((value) => value.id));
  }, [stageDefinitions]);

  // Filter stages to only show selected stages
  const displayedStages = stageDefinitions.filter((stage) =>
    stagesSelected.includes(stage.id)
  );

  const isStageTableLoading = useLoadVarietyStageReport(
    customerId,
    varietyId,
    dateRange
  );

  const isSizeTableLoading = useLoadVarietySizeReport(
    customerId,
    varietyId,
    dateRange
  );

  const { chartDates, loading: isChartDatesLoading } =
    useChartDates(isStageTableLoading);

  const displayLoading = useDebouncedLoading(
    isChartDatesLoading ||
      (viewBy === 'stage' ? isStageTableLoading : isSizeTableLoading)
  );

  // Navigate to block detail page when chart bar is clicked
  const handleBarClick = useCallback(
    (index: number, data: DatasetElementType<string | number>[]) => {
      const item = data.at(index);
      if (item) {
        navigate(`/blocks/${item.blockId}/scan/${item.scanId}`);
      }
    },
    [navigate]
  );

  const handleRanchToggle = useCallback(
    (activeRanchIds: string[]) => {
      setRanchesSelected(activeRanchIds);
    },
    [setRanchesSelected]
  );

  return (
    <CanopyProvider>
      <ControlPanel>
        <CustomerControlSection value={customerId} />
        <RanchToggleControlSection
          customerId={customerId}
          onRanchToggle={handleRanchToggle}
        />
        <ControlSection
          title={t('Filters')}
          icon={<FilterIcon color='secondary' fontSize='small' />}
        >
          <BaseControlItem label={t('View by')}>
            <Select
              variant='minimal'
              value={viewBy}
              onChange={setViewBy}
              options={[
                { value: 'stage', label: 'Stage' },
                { value: 'size', label: 'Size' },
              ]}
            />
          </BaseControlItem>
          {viewBy === 'stage' ? (
            <BaseControlItem label={t('Stage')}>
              <StageSelect
                value={stagesSelected}
                onChange={setStagesSelected}
                varietyId={varietyId || ''}
              />
            </BaseControlItem>
          ) : (
            <BaseControlItem label={t('Size')}>
              <SizeSelect value={sizesSelected} onChange={setSizesSelected} />
            </BaseControlItem>
          )}
        </ControlSection>
      </ControlPanel>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          overflowY: 'scroll',
        }}
      >
        <Stack
          sx={{
            padding: 6,
            gap: 8,
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            width: '100%',
            minWidth: 'min-content',
            height: 'min-content',
          }}
        >
          <Stack
            direction='row'
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <BetaWrapper>
              <Breadcrumbs
                customPaths={[
                  { label: t('Reports'), path: '/reports' },
                  {
                    label: variety?.name || '',
                    path: varietyId || '',
                  },
                ]}
              />
            </BetaWrapper>
            <Stack direction='row' sx={{ alignItems: 'center', gap: 1 }}>
              <DiagnosticFeature>
                <IconButton
                  size='small'
                  sx={(theme) => ({
                    color: theme.vars.palette.text.secondary,
                    padding: 2,
                    borderRadius: 1,
                  })}
                  onClick={() => setRawCountsOpen(true)}
                >
                  <DebugIcon />
                </IconButton>
              </DiagnosticFeature>
              {viewBy === 'stage' ? (
                <DownloadStageReportButton
                  customerId={customerId}
                  varietyId={varietyId}
                  shouldUpdate={!isStageTableLoading}
                  loading={displayLoading}
                />
              ) : (
                <DownloadSizeReportButton
                  customerId={customerId}
                  varietyId={varietyId}
                  shouldUpdate={!isSizeTableLoading}
                  loading={displayLoading}
                />
              )}
              <ReportsDateRangeSelect
                onChange={setDateRange}
                initialValue={dateRangeKey}
              />
            </Stack>
          </Stack>
          <CountModeToggleButtonGroup
            countMode={countMode}
            onCountModeChange={setCountMode}
            options={[
              { countMode: 'total' },
              { countMode: 'per_plant', disabled: viewBy === 'size' },
            ]}
          />

          <MenuPaper sx={{ width: '100%' }}>
            {displayLoading ? (
              <ChartsLoadingOverlay />
            ) : chartDates?.length ? (
              chartDates.map(({ scanDate, displayDate }, index) => {
                return (
                  <ReportChartContainer
                    key={scanDate}
                    displayDate={displayDate}
                    defaultExpanded={index === 0}
                  >
                    {viewBy === 'stage' ? (
                      <StageReportBarChart
                        scanDate={scanDate}
                        stages={displayedStages}
                        ranchIds={ranchesSelected}
                        tableLoading={displayLoading}
                        countMode={countMode}
                        onBarClick={handleBarClick}
                      />
                    ) : (
                      <SizeReportBarChart
                        scanDate={scanDate}
                        sizes={sizesSelected}
                        ranchIds={ranchesSelected}
                        tableLoading={displayLoading}
                        onBarClick={handleBarClick}
                      />
                    )}
                  </ReportChartContainer>
                );
              })
            ) : (
              <NoDataOverlay />
            )}
          </MenuPaper>
        </Stack>
      </Box>
      <RawCountsDialog
        open={rawCountsOpen}
        handleClose={() => setRawCountsOpen(false)}
        customerId={customerId}
        dateRange={dateRange ?? undefined}
      />
    </CanopyProvider>
  );
};
